import React, { useState, ComponentProps, useEffect } from 'react'
import { CircularProgress, Menu, MenuItem, Box } from '@mui/material'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Icon from 'components/Icon'
import idDirectory from './idAttributes'
import { useUiStateContext } from 'context/UiState/UiStateContext'
import { withRequiredRole } from 'components/useRequireRole'
import EntFeature from 'models/EntFeature'
import useFeatureToggle from 'hooks/FeatureToggles/useFeatureToggles'
import {
    AccentArea,
    // UploadRepresentmentsRouter,
    AlertSnackbar,
    AssignUser,
    CaseBuilderIssuer,
} from 'components'
import View from 'views/View'
import useCaseQCReview from './CaseQCReview.vm'
import { CaseInfo } from './sections/CaseInfo'
import { AddErt, DoNotRepresent, UploadCaseDocModal } from './modals'
import { useAuthedUser } from 'context/AuthedUser/AuthedUserContext'
import { SWRContextProvider as CaseQCReviewContextProvider } from 'context/SWRContext/SWRContext'
import { useParamsAGGrid, useCaseActions } from 'hooks/cb-api'
import { useHistory } from 'react-router'
import {
    ChevronIcon,
    CheckMarkIcon,
    EnvelopeIcon,
    QuestionCircleIcon,
    SplitArrowIcon,
    ScalesIcon,
    MoneyCycleIcon,
} from 'components/Icons'
import {
    PrimaryActionButton,
    SecondaryActionButton,
    CaseReivewDialog,
    CaseComments,
    ConfirmationModal,
} from './CaseReviewComponents'
import { useThemeStyling } from 'hooks/useThemeStyling/useThemeStyling'

const CaseQCReview = (): React.ReactElement => {
    const uiState = useUiStateContext()
    const { user } = useAuthedUser()
    const { clientTheme } = useThemeStyling()

    const { DO_NOT_REPRESENT } = useFeatureToggle('CASE_QC_REVIEW')
    const history = useHistory()

    const showClientTypeIssuerUI = user?.is_issuer ?? false

    const [openAddERTModal, setOpenAddERTModal] = useState<boolean>(false)
    const [openDNRModal, setOpenDNRModal] = useState<boolean>(false)
    const [openAssignUserModal, setOpenAssignUserModal] = useState<boolean>(
        false
    )
    const [openCreateCaseModal, setOpenCreateCaseModal] = useState<boolean>(
        false
    )
    const [
        disputeMenuAnchorEl,
        setDisputeMenuAnchorEl,
    ] = useState<null | HTMLElement>(null)
    const openDisputeMenu = Boolean(disputeMenuAnchorEl)

    const handleDisputeMenuClick = (
        event: React.MouseEvent<HTMLButtonElement>
    ) => {
        setDisputeMenuAnchorEl(event.currentTarget)
    }

    const handleDisputeMenuClose = () => {
        setDisputeMenuAnchorEl(null)
    }

    const showAddErtBtn = (uiState.whoami?.roles ?? []).some(
        (role) => role === EntFeature.QC_REVIEW_ADD_ERT
    )

    const showDNRBtn = DO_NOT_REPRESENT?.enabled ?? false

    const {
        alertSnackbarMainOpen,
        setAlertSnackbarMainOpen,
        alertSnackbarMainProps,
        setAlertSnackbarMainProps,
        documentInfo,
        uploadModalOpen,
        documentLoading,
        documentLoadKey,
        caseInfo,
        loadingCaseInfo,
        showApproveConfirm,
        errorGettingNewCase,
        errorGettingNewCaseText,
        isLoadingApproveQCReview,
        approveQCReview,
        getNextQCReviewCase,
        setUploadModalOpen,
        uploadModalButtonText,
        setUploadModalButtonText,
        // refreshPDFView,
        goBackToQCReviewGrid,
        setShowApproveConfirm,
        setRefreshCaseInfo,
        openDisputeModal,
        setOpenDisputeModal,
        disputeModalDetails,
        disputeTypeModal,
        setDisputeTypeModal,
        handlePostUploadChallengeResponse,
    } = useCaseQCReview()

    console.log('caseInfo', caseInfo)

    const { params, setParams, setQueryParams, reset } = useParamsAGGrid({})

    const paramSetter = { setParams, setQueryParams, params, reset }

    const viewInstance = {
        data: caseInfo,
        mutate: () => {},
    }

    const caseActions = useCaseActions(viewInstance, paramSetter)

    const actionMenuItems: {
        label: string
        icon: React.ReactNode
        onClick: () => void
        enabled: boolean
    }[] = [
        {
            label: 'Initiate Chargeback',
            icon: <EnvelopeIcon />,
            onClick: () => {
                setDisputeTypeModal('raise-chargeback')
                setOpenDisputeModal(true)
                handleDisputeMenuClose()
            },
            enabled: caseInfo.properties?.is_raise_chargeback_visible ?? false,
        },
        {
            label: 'Initiate Purchase Insight ',
            icon: <QuestionCircleIcon />,
            onClick: () => {
                setDisputeTypeModal('initiate-purchase-request')
                setOpenDisputeModal(true)
                handleDisputeMenuClose()
            },
            enabled:
                caseInfo.properties?.is_initiate_purchase_request_visible ??
                false,
        },
        {
            label: showClientTypeIssuerUI
                ? 'Initiate Split Request'
                : 'Initiate Merchant Request',
            icon: <SplitArrowIcon />,
            onClick: () => {
                setDisputeTypeModal('initiate-merchant-inquiry')
                setOpenDisputeModal(true)
                handleDisputeMenuClose()
            },
            enabled:
                caseInfo.properties?.is_initiate_merchant_inquiry_visible ??
                false,
        },
        {
            label: 'Initiate Split Request',
            icon: <SplitArrowIcon />,
            onClick: () => {
                setDisputeTypeModal('initiate-split-request')
                setOpenDisputeModal(true)
                handleDisputeMenuClose()
            },
            enabled:
                caseInfo.properties?.is_initiate_split_request_visible ?? false,
        },
        {
            label: showClientTypeIssuerUI
                ? 'Initiate PreArb'
                : 'Challenge Response',
            icon: <ScalesIcon />,
            onClick: () => {
                setUploadModalOpen(true)
                setUploadModalButtonText(
                    showClientTypeIssuerUI
                        ? 'Initiate PreArb'
                        : 'Challenge Response'
                )
                handleDisputeMenuClose()
            },
            enabled:
                caseInfo.properties?.is_challenge_response_visible ?? false,
        },
        {
            label: 'Initiate Arbritation',
            icon: <MoneyCycleIcon />,
            onClick: () => {
                handleDisputeMenuClose()
            },
            enabled: false, // not currently in use
        },
    ]

    return (
        <CaseQCReviewContextProvider SWRActions={caseActions}>
            <View
                title="Case Review"
                breadcrumb="Case Review"
                testId="caseQCReview"
            >
                {errorGettingNewCase && (
                    <AccentArea
                        containsFullWidthGrid={true}
                        hasHeaderDivider={false}
                        className={'emp-noMoreQCCases'}
                    >
                        <div className={'emp-noMoreQCCasesNotification'}>
                            {errorGettingNewCaseText}
                        </div>
                        <SecondaryActionButton onClick={goBackToQCReviewGrid}>
                            <ChevronIcon direction="left" />
                            Back
                        </SecondaryActionButton>
                    </AccentArea>
                )}
                {!loadingCaseInfo && !errorGettingNewCase && (
                    <AccentArea
                        testId="caseQCReview"
                        containsFullWidthGrid={true}
                        hasHeaderDivider={false}
                        className={'emp-adminCasesContainer'}
                    >
                        <div
                            className={'emp-headerContainer'}
                            id={idDirectory.divHeaderContainer}
                        >
                            <div className={'emp-headerContent'}>
                                <div>
                                    <SecondaryActionButton
                                        onClick={() => history.goBack()}
                                    >
                                        <ChevronIcon direction="left" />
                                        Back
                                    </SecondaryActionButton>
                                </div>

                                {/* Actions Container */}
                                <Box
                                    sx={(theme) => ({
                                        display: 'flex',
                                        gap: theme.spacing(1),
                                        justifyContent: 'flex-end',
                                        alignItems: 'center',
                                    })}
                                >
                                    {showAddErtBtn && (
                                        <SecondaryActionButton
                                            onClick={() =>
                                                setOpenAddERTModal(true)
                                            }
                                        >
                                            <FontAwesomeIcon icon={faPlus} />
                                            Add ERT
                                        </SecondaryActionButton>
                                    )}

                                    {/* {!showReviewPendingAIType &&
                                        showClientTypeIssuerUI && (
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                className={'emp-actionBtn'}
                                                onClick={() => {}}
                                            >
                                                <Icon
                                                    className={`far fa-file ${'emp-iconBtn'}`}
                                                />
                                                Decline
                                            </Button>
                                        )} */}

                                    {caseInfo.properties?.is_approve_visible &&
                                        (showApproveConfirm ? (
                                            <Box
                                                sx={(theme) => ({
                                                    display: 'flex',
                                                    gap: theme.spacing(1),
                                                })}
                                            >
                                                <PrimaryActionButton
                                                    onClick={approveQCReview}
                                                    disabled={
                                                        isLoadingApproveQCReview
                                                    }
                                                >
                                                    {isLoadingApproveQCReview ? (
                                                        <>
                                                            Accepting...{' '}
                                                            <CircularProgress
                                                                className={
                                                                    'emp-circularProgress'
                                                                }
                                                                color="secondary"
                                                            />
                                                        </>
                                                    ) : (
                                                        'Confirm Approval'
                                                    )}
                                                </PrimaryActionButton>

                                                <SecondaryActionButton
                                                    onClick={() =>
                                                        setShowApproveConfirm(
                                                            false
                                                        )
                                                    }
                                                >
                                                    Cancel
                                                </SecondaryActionButton>
                                            </Box>
                                        ) : (
                                            <>
                                                <PrimaryActionButton
                                                    onClick={() =>
                                                        setShowApproveConfirm(
                                                            true
                                                        )
                                                    }
                                                >
                                                    <Icon
                                                        className={`far fa-file ${'emp-iconBtn'}`}
                                                    />
                                                    Accept
                                                </PrimaryActionButton>

                                                <SecondaryActionButton
                                                    onClick={() => {
                                                        setUploadModalOpen(true)
                                                        setUploadModalButtonText(
                                                            'Challenge Response'
                                                        )
                                                    }}
                                                >
                                                    <Icon
                                                        className={`fas fa-circle-arrow ${'emp-iconBtn'}`}
                                                    />
                                                    Replace
                                                </SecondaryActionButton>
                                            </>
                                        ))}

                                    {/* Mark as Resolved - Primary Button */}
                                    {caseInfo.properties
                                        ?.is_marked_resolved_visible && (
                                        <PrimaryActionButton
                                            onClick={() => {
                                                setDisputeTypeModal(
                                                    'mark-as-resolved'
                                                )
                                                setOpenDisputeModal(true)
                                                handleDisputeMenuClose()
                                            }}
                                        >
                                            <CheckMarkIcon />
                                            Mark as Resolved
                                        </PrimaryActionButton>
                                    )}

                                    {/* Continue to Dispute - Dropdown Button */}
                                    {actionMenuItems.some(
                                        (item) => item.enabled
                                    ) && (
                                        <>
                                            <SecondaryActionButton
                                                onClick={handleDisputeMenuClick}
                                            >
                                                Continue to Dispute
                                                <ChevronIcon
                                                    direction={
                                                        openDisputeMenu
                                                            ? 'up'
                                                            : 'down'
                                                    }
                                                    style={{
                                                        transition:
                                                            'transform 0.15s ease',
                                                    }}
                                                />
                                            </SecondaryActionButton>

                                            <Menu
                                                anchorEl={disputeMenuAnchorEl}
                                                open={openDisputeMenu}
                                                onClose={handleDisputeMenuClose}
                                                anchorOrigin={{
                                                    vertical: 'bottom',
                                                    horizontal: 'right',
                                                }}
                                                transformOrigin={{
                                                    vertical: -4,
                                                    horizontal: 'right',
                                                }}
                                            >
                                                {actionMenuItems.map(
                                                    (item) =>
                                                        item.enabled && (
                                                            <MenuItem
                                                                key={item.label}
                                                                onClick={
                                                                    item.onClick
                                                                }
                                                                sx={(
                                                                    theme
                                                                ) => ({
                                                                    fontSize:
                                                                        clientTheme
                                                                            .typography
                                                                            .body1
                                                                            .fontSize,
                                                                    padding: theme.spacing(
                                                                        1,
                                                                        2
                                                                    ),
                                                                    display:
                                                                        'flex',
                                                                    alignItems:
                                                                        'center',
                                                                    justifyContent:
                                                                        'start',
                                                                    gap: theme.spacing(
                                                                        1
                                                                    ),
                                                                    '& :first-child': {
                                                                        width:
                                                                            '20px',
                                                                    },
                                                                })}
                                                            >
                                                                {item.icon}

                                                                {item.label}
                                                            </MenuItem>
                                                        )
                                                )}
                                            </Menu>
                                        </>
                                    )}
                                </Box>
                            </div>
                        </div>
                        <div className={'emp-gridContainer'}>
                            <div
                                className={'emp-caseInfoContainer'}
                                id={idDirectory.divCaseInfoContainer}
                            >
                                <CaseInfo
                                    caseInfo={caseInfo}
                                    showClientTypeIssuerUI={
                                        showClientTypeIssuerUI
                                    }
                                />
                            </div>
                            <div
                                className={'emp-casePreviewContainer'}
                                id={idDirectory.divCasePreviewContainer}
                            >
                                {documentLoading ? (
                                    <div
                                        className={
                                            'emp-pdfLoadingSpinnerContainer'
                                        }
                                    >
                                        <CircularProgress />
                                    </div>
                                ) : (
                                    <iframe
                                        className={'emp-documentIframe'}
                                        src={documentInfo.link}
                                        key={documentLoadKey}
                                        title="PDF Preview"
                                    >
                                        <div>
                                            No PDF viewer installed on system
                                        </div>
                                    </iframe>
                                )}
                            </div>
                        </div>
                        <div
                            className={'emp-footerContainer'}
                            id={idDirectory.divFooterContainer}
                        >
                            <div className={'emp-footerContent'}>
                                {!showClientTypeIssuerUI && (
                                    <>
                                        <div>
                                            {showDNRBtn && (
                                                <PrimaryActionButton
                                                    onClick={() =>
                                                        setOpenDNRModal(true)
                                                    }
                                                >
                                                    Do Not Represent
                                                </PrimaryActionButton>
                                            )}

                                            <SecondaryActionButton
                                                onClick={() => {
                                                    setUploadModalOpen(true)
                                                    setUploadModalButtonText(
                                                        'Challenge Response'
                                                    )
                                                }}
                                            >
                                                <Icon
                                                    className={`fas fa-circle-arrow ${'emp-iconBtn'}`}
                                                />
                                                Replace
                                            </SecondaryActionButton>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        <UploadCaseDocModal
                            open={uploadModalOpen}
                            primaryButton={{
                                text: uploadModalButtonText,
                                onClick: handlePostUploadChallengeResponse,
                            }}
                            secondaryButton={{
                                text: 'Cancel',
                                onClick: () => setUploadModalOpen(false),
                            }}
                            commentRequired
                            caseId={caseInfo.id}
                        />

                        {/* <UploadRepresentmentsRouter
                            cases={[caseInfo]}
                            caseIds={[caseInfo.id]}
                            openModal={uploadModalOpen}
                            toggleModal={() => {
                                if (uploadModalOpen) {
                                    setUploadModalOpen(false)
                                }
                            }}
                            isReupload={true}
                            rehydrateView={
                                showClientTypeIssuerUI
                                    ? undefined
                                    : () => {
                                          refreshPDFView()
                                      }
                            }
                            comments={documentInfo.case_comments}
                            setAlertSnackbarMainProps={
                                setAlertSnackbarMainProps
                            }
                            setAlertSnackbarMainOpen={setAlertSnackbarMainOpen}
                            disableRemedy
                            isRequiredCaseComment={
                                showClientTypeIssuerUI &&
                                caseInfo.properties
                                    ?.is_challenge_response_visible
                                    ? true
                                    : false
                            }
                            isRequiredFileUpload={
                                showClientTypeIssuerUI ? false : true
                            }
                            showEscalateCheckbox={
                                showClientTypeIssuerUI
                                    ? Boolean(
                                          caseInfo.properties
                                              ?.is_escalate_visible
                                      )
                                    : false
                            }
                            escalateCheckboxLabel={
                                showClientTypeIssuerUI
                                    ? 'Escalate to Chargeback'
                                    : ''
                            }
                            handlePostUploadAction={
                                showClientTypeIssuerUI
                                    ? handlePostUploadChallengeResponse
                                    : null
                            }
                            submitBtnText={
                                showClientTypeIssuerUI
                                    ? 'Challenge Response'
                                    : 'Upload'
                            }
                            showUploadIcon={false}
                        /> */}
                    </AccentArea>
                )}
                <AlertSnackbar
                    content={alertSnackbarMainProps}
                    open={alertSnackbarMainOpen}
                    onClose={() => setAlertSnackbarMainOpen(false)}
                    showCloseIcon
                />
                {openAddERTModal && (
                    <AddErt
                        openAddERT={openAddERTModal}
                        onClose={() => setOpenAddERTModal(false)}
                        caseId={caseInfo.id}
                        setRefreshCaseInfo={setRefreshCaseInfo}
                        setAlertSnackbarMainProps={setAlertSnackbarMainProps}
                        setAlertSnackbarMainOpen={setAlertSnackbarMainOpen}
                    />
                )}
                {openCreateCaseModal && (
                    <CaseBuilderIssuer
                        openCaseBuilderIssuer={openCreateCaseModal}
                        onClose={() => setOpenCreateCaseModal(false)}
                        testId={'caseQCReview'}
                    />
                )}
                {openDNRModal && (
                    <DoNotRepresent
                        openDNR={openDNRModal}
                        onClose={() => setOpenDNRModal(false)}
                        caseId={caseInfo.id}
                        getNextQCReviewCase={getNextQCReviewCase}
                        setAlertSnackbarMainProps={setAlertSnackbarMainProps}
                        setAlertSnackbarMainOpen={setAlertSnackbarMainOpen}
                        showClientTypeIssuerUI={showClientTypeIssuerUI}
                    />
                )}
                {openAssignUserModal && (
                    <AssignUser
                        selectedCaseIds={[]}
                        selectedCaseMerchantId={null}
                        open={openAssignUserModal}
                        toggleOpen={() =>
                            setOpenAssignUserModal((prev) => !prev)
                        }
                        row={caseInfo}
                        rehydrateView={() => {}}
                        unassign={false}
                        cases={[]}
                        setAlertSnackbarMainProps={setAlertSnackbarMainProps}
                        setAlertSnackbarMainOpen={setAlertSnackbarMainOpen}
                    />
                )}

                <DisputeModal
                    open={openDisputeModal}
                    title={disputeModalDetails[disputeTypeModal].title}
                    description={
                        disputeModalDetails[disputeTypeModal].description
                    }
                    primaryButton={{
                        text: disputeModalDetails[disputeTypeModal].title,
                        onClick: disputeModalDetails[disputeTypeModal].onSubmit,
                    }}
                    secondaryButton={{
                        text: 'Cancel',
                        onClick: () => setOpenDisputeModal(false),
                    }}
                />
            </View>
        </CaseQCReviewContextProvider>
    )
}

const DisputeModal = (
    props: ComponentProps<typeof CaseReivewDialog> & { description: string }
) => {
    const [comment, setComment] = useState<string>('')
    const [commentError, setCommentError] = useState<string>()

    useEffect(() => {
        setCommentError(undefined)
    }, [comment])

    const [
        openAreYouSureCloseModal,
        setOpenAreYouSureCloseModal,
    ] = useState<boolean>(false)

    function handleCancel() {
        if (comment) {
            setOpenAreYouSureCloseModal(true)
        } else {
            props.secondaryButton.onClick()
            setComment('')
        }
    }

    function handleSubmit() {
        if (comment) {
            props.primaryButton.onClick()
        } else {
            setCommentError('Comment is required')
        }
    }

    function handleCancelClose() {
        setOpenAreYouSureCloseModal(false)
    }

    function handleSureToDiscard() {
        props.secondaryButton.onClick()
        setOpenAreYouSureCloseModal(false)
        setComment('')
    }

    return (
        <>
            <CaseReivewDialog
                {...props}
                secondaryButton={{
                    ...props.secondaryButton,
                    onClick: handleCancel,
                }}
                primaryButton={{
                    ...props.primaryButton,
                    onClick: handleSubmit,
                    disabled: !comment?.length,
                }}
            >
                <p>{props.description}</p>
                <CaseComments
                    onChange={(e) => setComment(e.target.value)}
                    error={commentError}
                    required
                />
            </CaseReivewDialog>

            <ConfirmationModal
                open={openAreYouSureCloseModal}
                title={'Are You Sure?'}
                message={'The changes you made in this session will be lost.'}
                primaryButton={{
                    text: 'Discard',
                    onClick: handleSureToDiscard,
                }}
                secondaryButton={{ text: 'Cancel', onClick: handleCancelClose }}
            />
        </>
    )
}

export default withRequiredRole(EntFeature.QC_REVIEW, CaseQCReview)
